import { useId, useRef, type ChangeEvent } from 'react';
import { FileText, Upload } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DOCUMENT_BOX_COPY } from '../wizardCopy';
import { DOCUMENT_ACCEPT, formatFileSize, shortDate } from './fileCheck';

/**
 * Upload Brand Guideline Document, in step 1 under Upload and prefill (spec:87; web.md §5.1): one Word
 * document or PDF. A chosen file waits until Next, which uploads it (its progress shows here) and starts
 * reading it; another file replaces it. The document the last import read is named as the current one.
 */
export function DocumentBox({
  pendingFile,
  current,
  error,
  uploading,
  uploadProgress,
  disabled = false,
  onChoose,
  onRemove,
}: {
  /** Chosen, not uploaded yet. */
  pendingFile: File | null;
  /** The latest import's document. */
  current: { fileName: string; uploadedAt: string } | null;
  /** A file the check or the server refused, in words. */
  error: string | null;
  uploading: boolean;
  /** 0-1 while uploading; null when the browser cannot measure it. */
  uploadProgress: number | null;
  disabled?: boolean;
  onChoose: (file: File) => void;
  onRemove: () => void;
}) {
  const headingId = useId();
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared, so choosing the same file again (after Remove) is still a change.
    event.target.value = '';
    if (file) onChoose(file);
  };

  const percent = uploadProgress === null ? null : Math.round(uploadProgress * 100);

  return (
    <section aria-labelledby={headingId} className="space-y-3 rounded-xl border border-line bg-page p-4">
      <div>
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
          <h3 id={headingId} className="text-sm font-semibold text-ink-strong">
            {DOCUMENT_BOX_COPY.heading}
          </h3>
        </div>
        <p className="mt-1 text-sm text-ink-muted">{DOCUMENT_BOX_COPY.help}</p>
      </div>

      {current && !pendingFile && (
        <p className="text-sm text-ink-label">{DOCUMENT_BOX_COPY.current(current.fileName, shortDate(current.uploadedAt))}</p>
      )}

      {pendingFile && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink">
          <span className="min-w-0 break-words">
            {pendingFile.name} · {formatFileSize(pendingFile.size)}
          </span>
          <Button type="button" variant="plain" size="sm" disabled={disabled} onClick={onRemove}>
            {DOCUMENT_BOX_COPY.remove}
          </Button>
        </div>
      )}

      <label htmlFor={inputId} className="sr-only">
        {DOCUMENT_BOX_COPY.inputLabel}
      </label>
      <input
        ref={input}
        id={inputId}
        type="file"
        accept={DOCUMENT_ACCEPT}
        className="sr-only"
        // The button below is the control; the input stays out of the Tab order.
        tabIndex={-1}
        disabled={disabled}
        onChange={onPick}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => input.current?.click()}>
          <Upload className="h-4 w-4" aria-hidden />
          {pendingFile || current ? DOCUMENT_BOX_COPY.replace : DOCUMENT_BOX_COPY.choose}
        </Button>
        {/* Always in the page, so the progress is read out as it changes. */}
        <p role="status" className="text-sm text-ink-muted">
          {uploading ? DOCUMENT_BOX_COPY.uploading(percent) : null}
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-error-dark">
          {error}
        </p>
      )}
    </section>
  );
}
